import React, { useContext } from 'react';
import styled from 'styled-components/macro';

import { fontDefault, fontDefaultAlt, fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import MiniLessonGoButton from '@mirinae/lessons/components/buttons/MiniLessonGoButton';
import { FormattedInlineText, FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import { lessonsPalette, colorMap } from '@mirinae/lessons/modules/defines/styles';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import ProgressContext from '@mirinae/lessons/modules/contexts/progress';
import { HelpBubble, registerHelp } from '@mirinae/lessons/components/lesson/Help';

const ExampleKOStyle = { koFontWeight: 'medium', koreanFontSize: '16px' };

const ProgressBar = styled.div`
    position: absolute;
    left: 0;
    flex-shrink: 0;
    width: 100%;
    height: 5px;
    overflow: hidden;
`;
const ProgressBarInner = styled.div`
    position: absolute;
    top: 0;
    left: -1px;
    right: -1px;
    height: 10px;
    border-radius: 10px 10px 0 0;
    overflow: hidden;
    background: ${props => (props.progress === 100 ? 'linear-gradient(90deg, #CBDF8D 0%, #ACC564 50%, #87B600 100%)' : 'linear-gradient(90deg, #CBDFF2 0, #88ACD7 100%)')};
    &:before {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        display: block;
        width: ${props => (100 - props.progress)}%;
        height: 100%;
        background: ${lessonsPalette.grey[5]};
        overflow: hidden;
    }
`;
const SectionMissingMsg = styled.div`
    font-size: 10px;
    color: ${lessonsPalette.lessons[2]};
`;

const tableWidth = ['100%', '20%', '24%', '10%'];
const CardContentWrapper = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    ${props => (props.theme === 'space' && `
        padding-top: 7px;
    `)}
    ${!os.isOthers && `
        padding-right: 20px;
        padding-left: 20px;
    `}
`;
const Condition = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.w};
    padding: 0 6px;
    font-family: ${fontDefault};
    font-size: 16px;
    line-height: 20px;
    text-align: center;
    color: ${lessonsPalette.grey[0]};
`;
const Variant = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-family: ${fontkoKR};
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    color: ${colorMap.pattern}; // #F87313; // #FAD34A;
    min-width: fit-content;
    padding: 0 6px;
    ${props => os.isOthers && `
        width: ${props.w};
    `}
    &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        margin-top: -10px;
        width: 1px;
        height: 20px;
        background-color: ${lessonsPalette.grey[5]};
    }
    div {
        font-size: 15px;
        line-height: 20px;
    }
`;
const Examples = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0 8px;
    ${props => (os.isOthers ? `
        position: relative;
        width: ${props.w};
        &:after {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            margin-top: -10px;
            width: 1px;
            height: 20px;
            background-color: ${lessonsPalette.grey[5]};
        }
    ` : `
        position: absolute;
        left: 20px;
        bottom: 0;
        right: 20px;
        height: 47px;
        border-top: 1px dashed ${lessonsPalette.grey[5]};
    `)}
`;
const ExampleStack = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    ${os.isOthers ? `
        flex-direction: column;
    ` : `
        flex-direction: row;
        & > div:not(:last-child) {
            padding-right: 14px;
        }
    `}
`;

const Example = styled(FormattedKoPhrase)`
    font-family: ${fontkoKR};
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    min-width: 100px;
`;
const GoButton = styled.div`
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    & > * {
        flex-shrink: 0;
    }
    ${props => (os.isOthers ? `
        width: ${props.w};
        &:after {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            margin-top: -10px;
            width: 1px;
            height: 20px;
            background-color: ${lessonsPalette.grey[5]};
        }
    ` : `
        width: 40px;
    `)}
`;
const CardContent = ({ card }) => {
    const player = useContext(PlayerContext);
    const sectionName = card.fieldsByName.section.value;
    const section = player.findSection(sectionName);
    const progress = useContext(ProgressContext);

    return (
        <>
            { section && (
                <>
                    <ProgressBar>
                        <ProgressBarInner progress={progress.sections[section.nodeID] ? progress.sections[section.nodeID].progress : 0} />
                    </ProgressBar>
                </>
            )}
            <CardContentWrapper theme={section && 'space'}>
                <Condition w={tableWidth[0]}>
                    <FormattedText text={card.fieldsByName.condition.value} styles={card.fieldsByName.condition.styles} links={card.fieldsByName.condition.links} />
                </Condition>

                <Variant w={tableWidth[1]}>
                    {card.fieldsByName.variant.value}
                    { card.fieldsByName.annotation.value && <div>{card.fieldsByName.annotation.value}</div>}
                </Variant>

                <Examples w={tableWidth[2]}>
                    <ExampleStack>
                        { !card.fieldsByName.example1.hidden && (
                            <Example phrase={card.fieldsByName.example1} phraseStyle={ExampleKOStyle} />
                        )}
                        { !card.fieldsByName.example2.hidden && (
                            <Example phrase={card.fieldsByName.example2} phraseStyle={ExampleKOStyle} />
                        )}
                    </ExampleStack>
                </Examples>

                <GoButton w={tableWidth[3]}>
                    { section ? <MiniLessonGoButton section={section} /> : <SectionMissingMsg>Section<br />{sectionName}<br />missing</SectionMissingMsg> }
                </GoButton>
            </CardContentWrapper>
        </>
    );
};

const MiniLessonTableWrapper = styled.div`
    ${os.isOthers && `
        height: ${props => props.element.layout.row.height}px;
        display: flex;
    `}
`;

const MiniLessonTableElement = styled(Element)`
    display: flex;
    justify-content: center;
    min-height: auto;
    ${os.isOthers ? `
        flex-direction: row;
        margin: auto;
    ` : `
        flex-direction: column;
    `}
`;

const EndCapBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    background: ${lessonsPalette.grey[6]};
    border-radius: 10px;
    color: ${lessonsPalette.grey[1]};
    text-align: center;
    font-size: 12px;
    font-weight: 400;

    ${os.isOthers ? `
        max-width: 125px;
        margin-right: 15px;
        margin-top: 29px;
        padding: 0 10px;
        height: auto;
        line-height: 15px;
        min-height: 120px;
    ` : `
        width: 100%;
        line-height: 10px;
        padding: 15px;
        margin-bottom: 20px;
    `}
`;

const EndCapLabel = styled.div`
    margin-bottom: 8px;
`;

const EndCapText = styled.div`
    font-weight: 700;
    font-size: 12px;
    ${!os.isOthers && `
        font-size: 14px;
    `}
`;

const MiniLessonCard = styled.div`
    width: 100%;

    ${os.isOthers && `
        min-height: 120px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    `}
`;

const MiniLessonCardHead = styled.div`
    display: flex;
    height: 15px;
    margin-bottom: 14px;
    font-family: ${fontDefaultAlt};
    font-weight: 700;
    font-size: 12px;
    line-height: 15px;
    color: ${lessonsPalette.grey[2]};
    div {
        text-align: center;
        text-transform: capitalize;
        &:not(:first-child) {
            flex-shrink: 0;
        }
    }
`;
const MiniLessonCardBody = styled.div`
    display: flex;
    flex-direction: column;
    ${!os.isOthers && `
        margin-bottom: 25px;
    `}
`;
const CardContentRow = styled.div`
    position: relative;
    margin-bottom: 15px;
    border: 1px solid ${lessonsPalette.grey[5]};
    background-color: ${lessonsPalette.primary.white};
    border-radius: 10px;
    box-shadow: inset 6px 5px 20px rgba(244, 244, 244, 0.25), inset -6px -5px 20px rgba(186, 186, 186, 0.17);
    filter: drop-shadow(4px 2px 15px rgba(159, 188, 186, 0.2));
    &:last-child {
        margin-bottom: 0;
    }

    ${os.isOthers ? `
        height: 75px;
    ` : `
        height: 123px;
        padding-bottom: 47px;
    `}
`;
const MiniLessonTable = ({ element }) => {

    // gather miniLessonTableCards in my parent so we can control their layout (marked as managedByPage in lessonDefs, so default layout engine ignores them)
    const miniLessonTableCards = element.parent.children.filter(e => e.componentName === 'miniLessonTableCard'); // just element ordering .sort((c1, c2) => c1.geom.y - c2.geom.y);

    return (
        <MiniLessonTableWrapper element={element} className="mini-lesson-table">
            <MiniLessonTableElement element={element}>
                { !element.fieldsByName.endcapText.hidden && (
                    <EndCapBox>
                        <EndCapLabel>{element.fieldsByName.endcapLabel.value}</EndCapLabel>
                        <EndCapText><FormattedInlineText text={element.fieldsByName.endcapText.value} /></EndCapText>
                    </EndCapBox>
                )}

                <MiniLessonCard>
                    {os.isOthers && (
                        <MiniLessonCardHead>
                            <div style={{ width: tableWidth[0] }}>Condition</div>
                            <div style={{ width: tableWidth[1] }}>Variant</div>
                            <div style={{ width: tableWidth[2] }}>Examples</div>
                            <div style={{ width: tableWidth[3] }} />
                        </MiniLessonCardHead>
                    )}
                    <MiniLessonCardBody>
                        { miniLessonTableCards.map((mlc, i) => (
                            <CardContentRow key={i}>
                                <CardContent card={mlc} />
                            </CardContentRow>
                        ))}
                    </MiniLessonCardBody>
                </MiniLessonCard>
            </MiniLessonTableElement>
        </MiniLessonTableWrapper>
    );
};

registerHelp(MiniLessonTable, [
    <HelpBubble
        title="Mini-lesson table"
        desktop={{ anchor: '.mini-lesson-table', titleAt: 'bottom', arrowPos: 'left', arrowDir: 'down', style: { bottom: 265, left: 'calc(50% - 10px)', contentWidth: 280 } }}
        mobile={{ anchor: '.mini-lesson-table', titleAt: 'top', arrowPos: 'left', arrowDir: 'down', style: { top: -35, left: 40, contentWidth: 205 } }}>
        <p>This table summarizes all the variations of the lesson&apos;s pattern.</p>
        <p>You can click on a <strong>start button</strong> to take an optional mini-lesson on the variant. Progress in the mini-lesson will be shown in the blue progress bars
            here and on the mini-lesson pages.
        </p>
    </HelpBubble>,
]);

export default MiniLessonTable;
